import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from './useAuth';
import { SubscriptionService, UserSubscription, SubscriptionPlan, SUBSCRIPTION_PLANS } from '../lib/subscription';

// Cache for subscription data to reduce API calls
const subscriptionCache = new Map<string, { data: UserSubscription | null; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useSubscription = () => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscription = useCallback(async (forceRefresh = false) => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    // Check cache first unless forcing refresh
    if (!forceRefresh) {
      const cached = subscriptionCache.get(user.id);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        setSubscription(cached.data);
        setLoading(false);
        return;
      }
    }

    try {
      setLoading(true);
      const userSubscription = await SubscriptionService.getUserSubscription(user.id);
      setSubscription(userSubscription);
      setError(null);
      
      // Update cache
      subscriptionCache.set(user.id, {
        data: userSubscription,
        timestamp: Date.now()
      });
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const subscribe = async (
    planId: string, 
    interval: 'monthly' | 'yearly' = 'monthly'
  ): Promise<{ url: string } | null> => {
    if (!user?.id) {
      setError('User must be logged in to subscribe');
      return null;
    }

    try {
      setError(null);
      const result = await SubscriptionService.createSubscription(user.id, planId, interval);
      
      // Note: We don't refetch subscription here as the user will be redirected to Stripe
      // Subscription will be created via webhook after successful payment
      
      return result;
    } catch (err) {
      console.error('Error creating subscription:', err);
      setError('Failed to create subscription');
      return null;
    }
  };

  const cancel = async (cancelImmediately: boolean = false): Promise<{ 
    success: boolean; 
    canceledImmediately?: boolean; 
    currentPeriodEnd?: string 
  }> => {
    if (!user?.id) {
      setError('User must be logged in to cancel subscription');
      return { success: false };
    }

    try {
      setError(null);
      const result = await SubscriptionService.cancelSubscription(user.id, cancelImmediately);
      
      if (result.success) {
        // Invalidate cache and refetch subscription after cancellation
        subscriptionCache.delete(user.id);
        await fetchSubscription(true);
      }
      
      return result;
    } catch (err) {
      console.error('Error canceling subscription:', err);
      setError('Failed to cancel subscription');
      return { success: false };
    }
  };

  const openPortal = async (): Promise<{ url: string } | null> => {
    if (!user?.id) {
      setError('User must be logged in to access billing portal');
      return null;
    }

    try {
      setError(null);
      const result = await SubscriptionService.createPortalSession();
      return result;
    } catch (err) {
      console.error('Error opening billing portal:', err);
      setError('Failed to open billing portal');
      return null;
    }
  };

  const hasFeature = (feature: string): boolean => {
    return SubscriptionService.canAccessFeature(subscription, feature);
  };

  const getMaxChildren = (): number => {
    return SubscriptionService.getMaxChildren(subscription);
  };

  const getCurrentPlan = (): SubscriptionPlan | null => {
    if (!subscription) return null;
    return SubscriptionService.getPlanById(subscription.planId) || null;
  };

  const isActive = (): boolean => {
    return subscription?.status === 'active';
  };

  const isTrialing = (): boolean => {
    return subscription?.status === 'trialing';
  };

  const isLifetime = (): boolean => {
    return subscription?.status === 'lifetime';
  };

  const hasValidSubscription = useMemo(() => {
    return () => subscription?.status === 'active' || subscription?.status === 'trialing' || subscription?.status === 'lifetime';
  }, [subscription?.status]);

  const refetch = useCallback(() => {
    fetchSubscription(true); // Force refresh on manual refetch
  }, [fetchSubscription]);

  return {
    subscription,
    loading,
    error,
    subscribe,
    cancel,
    openPortal,
    hasFeature,
    getMaxChildren,
    getCurrentPlan,
    isActive,
    isTrialing,
    isLifetime,
    hasValidSubscription,
    plans: SUBSCRIPTION_PLANS,
    refetch
  };
};

export default useSubscription;